/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// Note: The listingFields come from listingFields asset nowadays by default.
//       To use this built-in configuration, you need to change the overwrite from configHelper.js
//       (E.g. use mergeDefaultTypesAndFieldsForDebugging func)

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - listingTypeConfig (optional):  Relationship configuration against listing types.
 *   - limitToListingTypeIds:         Indicator whether this listing field is relevant to a limited set of listing types.
 *   - listingTypeIds:                An array of listing types, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToListingTypeIds is true.
 * - categoryConfig (optional):     Relationship configuration against categories.
 *   - limitToCategoryIds:            Indicator whether this listing field is relevant to a limited set of categories.
 *   - categoryIds:                   An array of categories, for which this custom listing field is
 *                                    relevant and should be added. This is mandatory if limitToCategoryIds is true.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'oemNumber',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'OEM-numero',
    },
    saveConfig: {
      label: 'OEM-numero',
      placeholderMessage: 'Syötä OEM-numero',
      isRequired: true,
      requiredMessage: 'OEM-numero on pakollinen.',
    },
  },
  {
    key: 'partNumber',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Osanumero',
    },
    saveConfig: {
      label: 'Osanumero',
      placeholderMessage: 'Syötä osanumero',
      isRequired: true,
      requiredMessage: 'Osanumero on pakollinen.',
    },
  },
  {
    key: 'partName',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Osa Nimi',
    },
    saveConfig: {
      label: 'Osa Nimi',
      placeholderMessage: 'Syötä osan nimi',
      isRequired: true,
      requiredMessage: 'Osa nimi on pakollinen.',
    },
  },
  {
    key: 'partType',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Osa tyyppi',
    },
    saveConfig: {
      label: 'Osa tyyppi',
      placeholderMessage: 'Syötä osan tyyppi',
      isRequired: true,
      requiredMessage: 'Osa on pakollinen.',
    },
  },
  {
    key: 'partBrand',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Osa Brandi',
    },
    saveConfig: {
      label: 'Osa Brandi',
      placeholderMessage: 'Syötä osan brändi',
      isRequired: true,
      requiredMessage: 'Osa brändi on pakollinen.',
    },
  },
  {
    key: 'carType',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'SUV', label: 'SUV' },
      { option: 'Sedan', label: 'Sedan' },
      { option: 'Hatchback', label: 'Hatchback' },
      { option: 'Convertible', label: 'Convertible' },
    ],
    showConfig: {
      label: 'Auton Tyyppi',
    },
    saveConfig: {
      label: 'Auton Tyyppi',
      placeholderMessage: 'Auton tyyppi on pakollinen.',
      isRequired: true,
      requiredMessage: 'Auton tyyppi on pakollinen.',
    },
  },
  {
    key: 'partCondition',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'New', label: 'New' },
      { option: 'Used', label: 'Used' },
    ],
    showConfig: {
      label: 'Osa Kunto',
    },
    saveConfig: {
      label: 'Osa Kunto',
      placeholderMessage: 'Valitse osan kunto',
      isRequired: true,
      requiredMessage: 'Osa kunto on pakollinen.',
    },
  },
  {
    key: 'carBrand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bmw', label: 'BMW' },
      { option: 'audi', label: 'Audi' },
      { option: 'mercedes', label: 'Mercedes-Benz' },
      { option: 'toyota', label: 'Toyota' },
      { option: 'ford', label: 'Ford' },
      { option: 'chevrolet', label: 'Chevrolet' },
      { option: 'honda', label: 'Honda' },
      { option: 'nissan', label: 'Nissan' },
      { option: 'hyundai', label: 'Hyundai' },
      { option: 'kia', label: 'Kia' },
      { option: 'volkswagen', label: 'Volkswagen' },
      { option: 'subaru', label: 'Subaru' },
      { option: 'lexus', label: 'Lexus' },
      { option: 'mazda', label: 'Mazda' },
      { option: 'jeep', label: 'Jeep' },
      { option: 'dodge', label: 'Dodge' },
      { option: 'tesla', label: 'Tesla' },
      { option: 'porsche', label: 'Porsche' },
      { option: 'jaguar', label: 'Jaguar' },
      { option: 'land_rover', label: 'Land Rover' },
      { option: 'volvo', label: 'Volvo' },
      { option: 'acura', label: 'Acura' },
      { option: 'infiniti', label: 'Infiniti' },
      { option: 'lincoln', label: 'Lincoln' },
      { option: 'cadillac', label: 'Cadillac' },
      { option: 'buick', label: 'Buick' },
      { option: 'chrysler', label: 'Chrysler' },
      { option: 'fiat', label: 'Fiat' },
      { option: 'mini', label: 'Mini' },
      { option: 'mitsubishi', label: 'Mitsubishi' },
      { option: 'peugeot', label: 'Peugeot' },
      { option: 'renault', label: 'Renault' },
      { option: 'skoda', label: 'Skoda' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'alfa_romeo', label: 'Alfa Romeo' },
      { option: 'aston_martin', label: 'Aston Martin' },
      { option: 'bentley', label: 'Bentley' },
      { option: 'bugatti', label: 'Bugatti' },
      { option: 'ferrari', label: 'Ferrari' },
      { option: 'lamborghini', label: 'Lamborghini' },
      { option: 'maserati', label: 'Maserati' },
      { option: 'mclaren', label: 'McLaren' },
      { option: 'pagani', label: 'Pagani' },
      { option: 'rolls_royce', label: 'Rolls-Royce' },
    ],
    showConfig: {
      label: 'Car Brand',
    },
    saveConfig: {
      label: 'Car Brand',
      placeholderMessage: 'Valitse automerkki',
      isRequired: true,
      requiredMessage: 'Auton merkki on pakollinen.',
    },
  },
  // {
  //   key: 'carModel',
  //   scope: 'public',
  //   schemaType: 'text',
  //   showConfig: {
  //     label: 'Car Model',
  //   },
  //   saveConfig: {
  //     label: 'Car Model',
  //     placeholderMessage: 'Enter car model',
  //     isRequired: true,
  //     requiredMessage: 'Car model is required.',
  //   },
  // },
  {
    key: 'shipping',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      {
        option: 'Included',
        label: 'Included in the price of the nearest pick-up point in Finland',
      },
      {
        option: 'Agreed',
        label: 'To be agreed separately between the buyer and the seller',
      },
    ],
    showConfig: {
      label: 'Shipping Cost',
    },
    saveConfig: {
      label: 'Shipping Cost',
      placeholderMessage: 'Select shipping cost option',
      isRequired: true,
      requiredMessage: 'Shipping cost option is required.',
    },
  },
  {
    key: 'imageUrl',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Image URL',
    },
    saveConfig: {
      label: 'Image URL',
      placeholderMessage: 'Enter image URL',
    },
  },
  {
    key: 'source',
    scope: 'public',
    schemaType: 'text',
    showConfig: {
      label: 'Result Source',
    },
    saveConfig: {
      label: 'Result Source',
      placeholderMessage: 'Enter result source',
    },
  },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: The listingTypes come from listingTypes asset nowadays by default.
//         To use this built-in configuration, you need to change the overwrite from configHelper.js
//         (E.g. use mergeDefaultTypesAndFieldsForDebugging func)
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields These are tied to transaction processes. Different processes have different flags.
 *                        E.g. default-inquiry can toggle price and location to true/false value to indicate,
 *                        whether price (or location) tab should be shown. If defaultListingFields.price is not
 *                        explicitly set to _false_, price will be shown.
 *                        If the location or pickup is not used, listing won't be returned with location search.
 *                        Use keyword search as main search type if location is not enforced.
 *                        The payoutDetails flag allows provider to bypass setting of payout details.
 *                        Note: customers can't order listings, if provider has not set payout details! Monitor
 *                        providers who have not set payout details and contact them to ensure that they add the details.
 */

export const listingTypes = [
  // {
  //   listingType: 'daily-booking',
  //   label: 'Daily booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'day',
  //   },
  //   defaultListingFields: {
  //     location: true,
  //     payoutDetails: true,
  //   },
  // },
  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'product-selling',
  //   label: 'Sell bicycles',
  //   transactionType: {
  //     process: 'default-purchase',
  //     alias: 'default-purchase/release-1',
  //     unitType: 'item',
  //   },
  //   stockType: 'multipleItems',
  //   defaultListingFields: {
  //     shipping: true,
  //     pickup: true,
  //     payoutDetails: true,
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //     location: true,
  //   },
  // },
  {
    listingType: 'product-selling',
    label: 'Sell Car Parts',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-1',
      unitType: 'item',
    },
    stockType: 'multipleItems',
    defaultListingFields: {
      shipping: true,
      payoutDetails: true,
    },
  },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = true;
